import { useCallback, useEffect, useRef, useState } from 'react';
import {
  runCleanText,
  type CleanTextResult,
  type Refusal,
  type RunOptions,
  type RunSource,
} from './api';

export type Phase = 'input' | 'running' | 'result' | 'stopped';

/** Where an error is shown: under the drop zone, under the address, as the stop card, or a banner. */
export interface RunError {
  readonly where: 'file' | 'url' | 'stop' | 'banner';
  /** Key under `cleanText.errors` in the `tools` namespace. */
  readonly key: string;
  readonly values?: Readonly<Record<string, string | number>>;
  /** `TIMED_OUT` on a cold instance: the screen offers «Riprova». */
  readonly retry?: boolean;
}

/** The named steps, advanced on the client: v1 has no streaming, so they are estimated. */
export const STEP_NAMES = ['reading', 'finding', 'replacing', 'done'] as const;
/** When the estimate moves on from step 0 and from step 1, in ms after the start. */
export const STEP_DELAYS_MS: readonly [number, number] = [3000, 12000];

// Decisions 158 and 159.
const DEFAULT_LIMITS = { bytes: 5, pages: 12, characters: 120000, cells: 400 } as const;

const banner = (key: string): RunError => ({ where: 'banner', key });

/** The codes whose place and words do not depend on the refusal's details. */
const FIXED_REFUSALS: ReadonlyMap<string, RunError> = new Map(
  Object.entries({
    UNSUPPORTED_TYPE: { where: 'file', key: 'unsupportedType' },
    URL_REFUSED: { where: 'url', key: 'urlRefused' },
    URL_FETCH_FAILED: { where: 'url', key: 'urlFetchFailed' },
    DOCUMENT_UNREADABLE: { where: 'stop', key: 'unreadable' },
    ACTIVE_CONTEXT_REQUIRED: banner('noContext'),
    AUTHORIZATION_UNAVAILABLE: banner('unavailable'),
    PERMISSION_DENIED: banner('forbidden'),
    MODULE_NOT_ENTITLED: banner('forbidden'),
    UNAUTHENTICATED: banner('unauthenticated'),
  }),
);

/** The same, for a response with no usable code: the status says what it can. */
const STATUS_REFUSALS: ReadonlyMap<number, RunError> = new Map([
  [503, banner('unavailable')],
  [403, banner('forbidden')],
  [401, banner('unauthenticated')],
]);

function tooLargeError(refusal: Refusal, where: 'url' | 'file'): RunError {
  const exceeded = refusal.exceeded ?? 'bytes';
  const raw = refusal.limits?.[exceeded];
  let limit: number;
  if (exceeded !== 'bytes') limit = raw ?? DEFAULT_LIMITS[exceeded];
  else limit = raw === undefined ? DEFAULT_LIMITS.bytes : Math.round(raw / (1024 * 1024));
  return { where, key: `tooLarge.${exceeded}`, values: { limit } };
}

/** Maps every contract code to the place and the words of its message. */
export function describeRefusal(refusal: Refusal, source: RunSource['kind']): RunError {
  const here = source === 'url' ? 'url' : 'file';
  if (refusal.code === 'DOCUMENT_TOO_LARGE') return tooLargeError(refusal, here);
  if (refusal.code === 'INVALID_REQUEST') return { where: here, key: 'invalidRequest' };
  if (refusal.code === 'TIMED_OUT') {
    return refusal.retryable
      ? { where: 'banner', key: 'timedOutRetry', retry: true }
      : banner('timedOut');
  }
  const fixed = FIXED_REFUSALS.get(refusal.code ?? '');
  if (fixed) return fixed;
  return STATUS_REFUSALS.get(refusal.status) ?? banner('generic');
}

/**
 * One run: a single multipart request answered with JSON, an `AbortController` for «Annulla», and
 * the four named steps advanced by timers while the request is out.
 */
export function useCleanTextRun() {
  const [phase, setPhase] = useState<Phase>('input');
  const [step, setStep] = useState(0);
  const [result, setResult] = useState<CleanTextResult | null>(null);
  const [error, setError] = useState<RunError | null>(null);
  const abort = useRef<AbortController | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  useEffect(
    () => () => {
      clearTimers();
      abort.current?.abort();
    },
    [clearTimers],
  );

  const start = useCallback(
    async (source: RunSource, options: RunOptions): Promise<void> => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      clearTimers();
      setError(null);
      setResult(null);
      setStep(0);
      setPhase('running');
      timers.current = [
        setTimeout(() => setStep(1), STEP_DELAYS_MS[0]),
        setTimeout(() => setStep(2), STEP_DELAYS_MS[1]),
      ];

      const outcome = await runCleanText(source, options, controller.signal);
      if (abort.current !== controller) return; // cancelled, or replaced by a newer run
      clearTimers();
      abort.current = null;
      switch (outcome.kind) {
        case 'ok':
          setResult(outcome.result);
          setPhase('result');
          return;
        case 'aborted':
          setPhase('input');
          return;
        case 'network':
          setError({ where: 'banner', key: 'network', retry: true });
          setPhase('input');
          return;
        case 'refused': {
          const described = describeRefusal(outcome.refusal, source.kind);
          setError(described);
          setPhase(described.where === 'stop' ? 'stopped' : 'input');
        }
      }
    },
    [clearTimers],
  );

  /** «Annulla»: aborts the request and goes back to the input as it was. */
  const cancel = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    clearTimers();
    setPhase('input');
  }, [clearTimers]);

  const backToInput = useCallback(() => {
    setError(null);
    setResult(null);
    setPhase('input');
  }, []);

  return { phase, step, result, error, setError, start, cancel, backToInput };
}
